import React from "react";
import { FilterByTypes, fonts, Theme, timeOptions, WizardState } from "./models";
import { DetailSectionTemplate } from "components/MainSectionTemplate/DetailSectionTemplate";
import ThemeDropdown, { getThemeDropdownLabel } from "./components/themedropdown";
import { RenderEditOptions } from "components/MainSectionTemplate/types";
import { emptySubmitChangeFunc } from "components/MainSectionTemplate/emptySubmitChangeFunc";
import CultureLabel from "components/Culture/CultureLabel";
import CulturesInterfaceDropdown from "components/dropdowns/reactselect/CulturesInterfaceDropdown";
import { FontDropdown, PastShowsDropdown, UpcomingShowsDropdown } from "./components";
import RequiredNumberInput from "components/ValidatedFields/RequiredNumberInput";
import { YesNoRadioGroupForInPageEditing, YesNoText } from "components/ValidatedFields/YesNoRadioGroup";
import { FilterByRadio } from "./components/FilterByRadio";
import { ArtistMultiModalPicker, SelectedArtists } from "components/Picker/ArtistPicker/ArtistMultiModalPicker";
import { ProductionMultiModalPicker, SelectedProductions } from "components/Picker/ProductionPicker/ProductionMultiModalPicker";

interface WizardProps {
  state: WizardState
  setState: (newState: WizardState) => void
}

export const Wizard = (props: WizardProps) => {
  const onFilterByChange = (value: FilterByTypes) => {
    props.setState({
      ...props.state,
      filterBy: value,
      artists: [],
      productions: [],
    })
  }


  const onValueChanged = (key: string, value: any) => {
    if (key === "theme") {
      props.setState({ ...props.state, theme: value as Theme });
    } else if (key === "culture") {
      props.setState({ ...props.state, culture: value as server.dto.Culture });
    } else if (key === "font") {
      props.setState({ ...props.state, font: value });
    } else if (key === "perPage") {
      props.setState({ ...props.state, perPage: value });
    } else if (key === "upcomingShows") {
      props.setState({ ...props.state, upcomingShowDays: value });
    } else if (key === "pastShows") {
      props.setState({ ...props.state, pastShowsDays: value });
    } else if (key === "displayPagination") {
      props.setState({ ...props.state, displayPagination: value });
    } else if (key === "displayHeader") {
      props.setState({ ...props.state, displayHeader: value });
    } else if (key === "artist") {
      props.setState({ ...props.state, artists: value });
    } else if (key === "production") {
      props.setState({ ...props.state, productions: value });
    } else if (key === "includeCancelled") {
      props.setState({ ...props.state, includeCancelled: value });
    }
  }

  return <DetailSectionTemplate
    isInPageEditable
    onValueChanged={onValueChanged}
    sections={[{
      key: "embedShowsWizard",
      type: "regular",
      title: RESX.EmbedWizard.EmbedShows,
      fields: [{
        key: "theme",
        label: RESX.EmbedWizard.Theme,
        render: () => <>{getThemeDropdownLabel(props.state.theme)}</>,
        inPageEdit: {
          render: ({ value, onChange, cancel }: RenderEditOptions<Theme>) => <ThemeDropdown
            value={value}
            onChange={(culture) => {
              onChange(culture, true);
            }}
            onEsc={() => cancel()}
            config={{
              focusOnMount: true,
              openMenuOnFocus: true,
            }}
          />,
          fullWidth: true,
          type: "custom",
          initialValue: props.state.theme,
          submitChange: emptySubmitChangeFunc,
        }
      }, {
        key: "culture",
        label: RESX.Language.resxLanguage,
        render: () => <CultureLabel culture={props.state.culture} />,
        inPageEdit: {
          render: ({ value, onChange }) => <CulturesInterfaceDropdown
            value={value}
            onChange={v => onChange(v, true)}
            config={{
              focusOnMount: true,
              openMenuOnFocus: true
            }}
          />,
          type: "confirmOrCancelButton",
          initialValue: props.state.culture,
          fullWidth: true,
          submitChange: emptySubmitChangeFunc,
        }
      }, {
        key: "font",
        label: RESX.EmbedWizard.Font,
        render: () => <>{props.state.font}</>,
        inPageEdit: {
          render: ({ value, onChange }) => <FontDropdown
            value={value}
            onChange={v => onChange(v, true)}
            config={{
              focusOnMount: true,
              openMenuOnFocus: true
            }}
            options={fonts}
          />,
          fullWidth: true,
          type: "custom",
          initialValue: props.state.font,
          submitChange: emptySubmitChangeFunc,
        }
      }, {
        key: "perPage",
        label: RESX.EmbedWizard.PerPage,
        render: () => <>{props.state.perPage} {site.currentEnvironment.show.plural}</>,
        inPageEdit: {
          render: ({ value, onChange, cancel }: RenderEditOptions<number>) => <RequiredNumberInput
            autoFocus
            width={45}
            validatorKey="perPage"
            value={value}
            onChange={(v: number) => onChange(v)}
            onEnterPressed={(v: number) => onChange(v, true)}
            onEscPressed={() => cancel()}
            decimalPoints={0}
            minValue={5}
            maxValue={50}
            labelContent={site.currentEnvironment.show.plural}
          />,
          fullWidth: false,
          type: "confirmOrCancelButton",
          initialValue: props.state.perPage,
          submitChange: emptySubmitChangeFunc,
        }
      }, {
        key: "upcomingShows",
        label: RESX.EmbedWizard.UpcomingShows,
        render: () => <>{timeOptions.find(o => o.value === props.state.upcomingShowDays).text}</>,
        inPageEdit: {
          render: ({ value, onChange }) => <UpcomingShowsDropdown
            value={value}
            onChange={v => onChange(v, true)}
            config={{
              focusOnMount: true,
              openMenuOnFocus: true
            }}
          />,
          fullWidth: true,
          type: "custom",
          initialValue: props.state.upcomingShowDays,
          submitChange: emptySubmitChangeFunc,
        }
      }, {
        key: "pastShows",
        label: RESX.EmbedWizard.PastShows,
        isEmpty: props.state.pastShowsDays === 0,
        render: () => <>{timeOptions.find(o => o.value === props.state.pastShowsDays)?.text}</>,
        inPageEdit: {
          render: ({ value, onChange }) => <PastShowsDropdown
            value={value}
            onChange={v => onChange(v, true)}
            config={{
              focusOnMount: true,
              openMenuOnFocus: true
            }}
          />,
          fullWidth: true,
          type: "custom",
          initialValue: props.state.pastShowsDays,
          submitChange: emptySubmitChangeFunc,
        }
      }, {
        key: "displayPagination",
        label: RESX.EmbedWizard.Pagination,
        render: () => <YesNoText value={props.state.displayPagination} />,
        inPageEdit: {
          render: ({ value, onChange }: RenderEditOptions<boolean>) => <YesNoRadioGroupForInPageEditing
            value={value}
            onChange={(v) => onChange(v, true)}
          />,
          fullWidth: true,
          type: "custom",
          initialValue: props.state.displayPagination,
          submitChange: emptySubmitChangeFunc,
        },
      }, {
        key: "displayHeader",
        label: RESX.EmbedWizard.Header,
        render: () => <YesNoText value={props.state.displayHeader} />,
        inPageEdit: {
          render: ({ value, onChange }: RenderEditOptions<boolean>) => <YesNoRadioGroupForInPageEditing
            value={value}
            onChange={(v) => onChange(v, true)}
          />,
          fullWidth: true,
          type: "custom",
          initialValue: props.state.displayHeader,
          submitChange: emptySubmitChangeFunc,
        },
      }, {
        key: "filterBy",
        label: RESX.EmbedWizard.Display,
        render: () => <FilterByRadio
          filterType={props.state.filterBy}
          onChange={onFilterByChange}
        />
      }, {
        key: "artist",
        label: site.currentEnvironment.functionGroups.artist.singular,
        isEmpty: props.state.artists.length === 0,
        hidden: props.state.filterBy !== "MultipleArtists",
        render: () => <SelectedArtists value={props.state.artists} />,
        inPageEdit: {
          fullWidth: true,
          disableClickOutside: true,
          render: ({ value, onChange, cancel }) => <div className="main-section-template-fields-padding">
            <ArtistMultiModalPicker
              securityScope="limitByUserAccess"
              inPageEditing={{ cancel }}
              value={value}
              onChange={(v) => {
                onChange(v, true);
              }}
              canCreateNew={false}
              required={false}
              validatorKey="artists"
            />
          </div>,
          initialValue: props.state.artists,
          type: "custom",
          submitChange: emptySubmitChangeFunc,
        }
      }, {
        key: "production",
        label: site.currentEnvironment.production.singular,
        isEmpty: props.state.productions.length === 0,
        hidden: props.state.filterBy !== "Productions",
        render: () => <SelectedProductions value={props.state.productions} />,
        inPageEdit: {
          fullWidth: true,
          disableClickOutside: true,
          render: ({ value, onChange, cancel }) => <ProductionMultiModalPicker
            inPageEditing={{ cancel }}
            value={value}
            onChange={v => onChange(v, true)}
            canCreateNew={false}
            required={false}
            validatorKey="productions"
          />,
          initialValue: props.state.productions,
          type: "custom",
          submitChange: emptySubmitChangeFunc,
        }
      }, {
        key: "includeCancelled",
        label: RESX.Status.IncludeCancelled,
        render: () => <YesNoText value={props.state.includeCancelled} />,
        inPageEdit: {
          render: ({ value, onChange }: RenderEditOptions<boolean>) => <YesNoRadioGroupForInPageEditing
            value={value}
            onChange={(v) => onChange(v, true)}
          />,
          fullWidth: true,
          type: "custom",
          initialValue: props.state.includeCancelled,
          submitChange: emptySubmitChangeFunc,
        },
      }]
    }]}
  />
}